import { useEffect, useMemo, useState } from 'react';

/**
 * Hook genérico de tabla: búsqueda + orden + paginación en memoria. Reemplaza la
 * lógica que hoy se duplica en cada modal de drill-down (ver ANALISIS-CRITICO H3).
 *
 * El componente decide CÓMO buscar/ordenar (callbacks); el hook solo orquesta el
 * estado. Sin lógica de negocio.
 */

export type SortDir = 'asc' | 'desc';

export interface UseDataTableOptions<T> {
  items: T[];
  /** Campos por los que se busca (texto libre). */
  search?: (item: T) => Array<string | number | null | undefined>;
  /** Valor ordenable de un item para una columna dada. */
  sortValue?: (item: T, key: string) => string | number | null | undefined;
  initialSortKey?: string;
  initialSortDir?: SortDir;
  pageSize?: number;
}

export interface UseDataTableResult<T> {
  query: string;
  setQuery: (q: string) => void;
  sortKey: string;
  sortDir: SortDir;
  toggleSort: (key: string) => void;
  page: number;
  setPage: (p: number) => void;
  pageCount: number;
  /** Filas de la página actual. */
  rows: T[];
  /** Total tras filtrar (antes de paginar). */
  total: number;
  /** Todas las filas filtradas+ordenadas (para exportar). */
  all: T[];
}

export function useDataTable<T>({
  items,
  search,
  sortValue,
  initialSortKey = '',
  initialSortDir = 'desc',
  pageSize = 50,
}: UseDataTableOptions<T>): UseDataTableResult<T> {
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState(initialSortKey);
  const [sortDir, setSortDir] = useState<SortDir>(initialSortDir);
  const [page, setPage] = useState(0);

  // Cualquier cambio de filtro/orden/datos vuelve a la primera página.
  useEffect(() => setPage(0), [query, sortKey, sortDir, items]);

  const all = useMemo(() => {
    const term = query.trim().toLowerCase();
    let rows = items;

    if (term && search) {
      rows = rows.filter((item) =>
        search(item).some((f) => String(f ?? '').toLowerCase().includes(term)),
      );
    }

    if (sortKey && sortValue) {
      const dir = sortDir === 'asc' ? 1 : -1;
      rows = [...rows].sort((a, b) => {
        const va = sortValue(a, sortKey);
        const vb = sortValue(b, sortKey);
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        return String(va ?? '').localeCompare(String(vb ?? ''), 'es') * dir;
      });
    }

    return rows;
  }, [items, query, search, sortKey, sortDir, sortValue]);

  const pageCount = Math.max(1, Math.ceil(all.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const rows = all.slice(safePage * pageSize, safePage * pageSize + pageSize);

  function toggleSort(key: string): void {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  return {
    query,
    setQuery,
    sortKey,
    sortDir,
    toggleSort,
    page: safePage,
    setPage,
    pageCount,
    rows,
    total: all.length,
    all,
  };
}
